/**
 * SowFidelityPanel - read-only summary of the per-dimension visual fidelity of a
 * generated SOW template vs its canonical example. Rendered above the workbench
 * config when the fidelity loop ran (template.metadata.fidelity.profileConformance).
 *
 * Display only: the report is already in template.metadata (no fetch). Styling is
 * token-driven (see product-template-page.css `.sow-fidelity-panel__*`).
 */

import * as React from 'react';

export type SowFidelityStatus = 'pass' | 'warn' | 'fail';

export interface SowFidelityCheck {
  key: string;
  label: string;
  status: SowFidelityStatus;
  detail: string;
  weight: number;
}

export interface SowFidelityReport {
  score: number;
  summary: string;
  checks: SowFidelityCheck[];
}

export interface SowFidelityPlaceholderSpan {
  sample: string;
  kind: 'placeholder' | 'editorial';
  highlighted: boolean;
}

/** The fidelity slice of a SOW template's metadata (alongside warnings/stats). */
export interface SowFidelityMetadata {
  profileConformance?: SowFidelityReport;
  repairsApplied?: string[];
  placeholderSpans?: SowFidelityPlaceholderSpan[];
}

const prettyKey = (key: string): string => key.replace(/_/g, ' ');

function scoreLevel(pct: number): SowFidelityStatus {
  if (pct >= 99) return 'pass';
  if (pct >= 80) return 'warn';
  return 'fail';
}

export function SowFidelityPanel({
  fidelity,
}: {
  fidelity?: SowFidelityMetadata | null;
}): React.ReactElement | null {
  const report = fidelity?.profileConformance;
  if (!report) return null;

  const pct = Math.round(report.score * 100);
  const level = scoreLevel(pct);
  const issues = report.checks.filter((c) => c.status !== 'pass');
  const passes = report.checks.length - issues.length;
  const repairs = fidelity?.repairsApplied ?? [];
  const spans = fidelity?.placeholderSpans ?? [];
  const placeholders = spans.filter((s) => s.kind === 'placeholder').length;
  const editorial = spans.filter((s) => s.kind === 'editorial').length;

  return (
    <section className="sow-fidelity-panel" aria-label="Visual fidelity vs the base document">
      <header className="sow-fidelity-panel__header">
        <span className={`sow-fidelity-panel__score sow-fidelity-panel__score--${level}`}>
          {pct}%
        </span>
        <span className="sow-fidelity-panel__summary">{report.summary}</span>
      </header>

      {repairs.length > 0 && (
        <p className="sow-fidelity-panel__repairs">
          Auto-repaired from the base document: {repairs.map(prettyKey).join(', ')}.
        </p>
      )}

      {issues.length > 0 ? (
        <ul className="sow-fidelity-panel__checks">
          {issues.map((c) => (
            <li
              key={c.key}
              className={`sow-fidelity-panel__check sow-fidelity-panel__check--${c.status}`}
            >
              <span className="sow-fidelity-panel__check-status">{c.status}</span>
              <span className="sow-fidelity-panel__check-label">{c.label}</span>
              <span className="sow-fidelity-panel__check-detail">{c.detail}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="sow-fidelity-panel__all-pass">All {passes} fidelity checks pass.</p>
      )}

      {(placeholders > 0 || editorial > 0) && (
        <p className="sow-fidelity-panel__spans">
          Found {placeholders} placeholder{placeholders === 1 ? '' : 's'} and {editorial} editorial
          instruction{editorial === 1 ? '' : 's'} in the base document - map the placeholders and
          remove the instructions when confirming the sections.
        </p>
      )}
    </section>
  );
}
